import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { useAsyncResult } from "../../Hooks/UseAsyncResult";
import { fetchExtensionExperiments, fetchAdoContent } from "../../Api/ado.api";
import { ExtensionFolder, RolloutTemplate } from "../../Types";
import { parseStageType } from "../../Experiment/RolloutDetails/RolloutDetailsForm/RolloutFormFieldRenderer";
import { useEv2ExperimentParams } from "../UseCurrentExtension";
import yaml from "js-yaml";

type RolloutTemplateOption = { key: string; text: string } | null;

export function useFetchRolloutTemplates(
  extensionName: string | undefined,
  setError: Dispatch<SetStateAction<any>>,
) {
  const [templateMap, setTemplateMap] = useState<
    Record<string, RolloutTemplate>
  >({});
  const [selectedRolloutTemplate, setSelectedRolloutTemplate] =
    useState<RolloutTemplateOption>({ key: "", text: "" });
  const { extensionFolder } = useEv2ExperimentParams();
  const func = useCallback(
    async (_disposable, _force) => {
      if (!extensionName || !extensionFolder) {
        return [];
      }
      const { rootExtension } = extensionFolder;
      const pipelinePath = `${rootExtension}/.pipelines`;
      const files = (await fetchExtensionExperiments(pipelinePath)) || [];
      const ymlFiles = files.filter(
        (item: ExtensionFolder) => !item.isFolder && item.path.endsWith(".yml"),
      );

      const rawContents = await Promise.all(
        ymlFiles.map((file) => fetchAdoContent(file)),
      );

      const templates: RolloutTemplate[] = rawContents.map((content) => {
        const parsed = yaml.load(content.__raw) as Record<string, any>;
        return {
          ...content,
          ...parsed,
        } as RolloutTemplate;
      });

      const templateMap = templates.reduce<Record<string, RolloutTemplate>>(
        (map, template) => {
          if (!map[template.__filename]) {
            map[template.__filename] = template;
          }
          return map;
        },
        {},
      );

      setTemplateMap(templateMap);
      setSelectedRolloutTemplate({
        key: templates[0].__filename,
        text: parseStageType(templates[0].__filename),
      });
      return templates;
    },
    [extensionFolder, extensionName],
  );
  const asyncResults = useAsyncResult({
    func,
    setError,
  });
  return {
    ...asyncResults,
    templateMap,
    setSelectedRolloutTemplate,
    selectedRolloutTemplate,
  };
}
